import React, { useMemo } from "react";
import { LineGeometry, LineMaterial, LineSegments2 } from "three-stdlib";
import { OutlineSegment } from "../../types/floorplannerTypes";
import { ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { getWallInteractionHandlers } from "../../utils/wallInteractionRegistry";
import { editorStore } from "../../store/editorStore";

interface WallOutlineSegmentProps {
  segment: OutlineSegment;
  visible?: boolean;
  getCachedMaterial?: (color: number, linewidth: number, dashed: boolean, resolution: THREE.Vector2) => LineMaterial;
}

/**
 * Component that renders a single outline segment as a line with proper thickness and styling.
 * Uses LineSegments2 from three-stdlib for better line rendering quality.
 */
const WallOutlineSegment: React.FC<WallOutlineSegmentProps> = ({ segment, visible = true, getCachedMaterial }) => {
  const canvasWidth = useThree((state) => state.size.width);
  const canvasHeight = useThree((state) => state.size.height);

  // Create LineGeometry for the segment
  const geometry = useMemo(() => {
    // const segmentLength = Math.sqrt(
    //   Math.pow(segment.end.x - segment.start.x, 2) +
    //   Math.pow(segment.end.y - segment.start.y, 2)
    // );
    // console.log(`  WallOutlineSegment geometry:`, {
    //   segmentId: segment.id,
    //   wallId: segment.wallId,
    //   side: segment.side,
    //   start: `(${segment.start.x.toFixed(3)}, ${segment.start.y.toFixed(3)})`,
    //   end: `(${segment.end.x.toFixed(3)}, ${segment.end.y.toFixed(3)})`,
    //   length: segmentLength.toFixed(3),
    //   z: 0.001,
    // });
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([
      segment.start.x,
      segment.start.y,
      0.001, // z-coordinate for outline segments
      segment.end.x,
      segment.end.y,
      0.001, // z-coordinate for outline segments
    ]);
    return lineGeometry;
  }, [segment.start, segment.end, segment.id, segment.wallId, segment.side]);

  // Create or get cached LineMaterial for the segment
  const material = useMemo(() => {
    const resolution = new THREE.Vector2(canvasWidth, canvasHeight);

    // Use cached material if available
    if (getCachedMaterial) {
      return getCachedMaterial(segment.color, segment.thickness, segment.dashed, resolution);
    }

    // Fallback: create new material (for backward compatibility)
    const lineMaterial = new LineMaterial({
      color: segment.color,
      linewidth: segment.thickness,
      // CRITICAL: Set resolution for line thickness to work
      resolution,
    });

    if (segment.dashed) {
      lineMaterial.dashed = true;
      // Use world units for dash size to maintain consistent appearance
      lineMaterial.dashSize = 0.3;
      lineMaterial.gapSize = 0.15;
    }

    // Force update the material
    lineMaterial.needsUpdate = true;

    return lineMaterial;
  }, [segment.color, segment.thickness, segment.dashed, canvasWidth, canvasHeight, getCachedMaterial]);

  // Create LineSegments2 object
  const lineSegment = useMemo(() => {
    const segments = new LineSegments2(geometry, material);
    segments.position.set(0, 0, 0.001); // z-index for outlines (above fills at z=0, below measurements)

    // const segmentLength = Math.sqrt(
    //   Math.pow(segment.end.x - segment.start.x, 2) +
    //   Math.pow(segment.end.y - segment.start.y, 2)
    // );
    // // Check if segment is at a connection point (gap indicator)
    // // A gap would be indicated by a very short segment or segments that don't span the full wall
    // const isAtConnection = segmentLength < 0.05; // Very short segments are likely at connection points

    // console.log(`  WallOutlineSegment lineSegment:`, {
    //   segmentId: segment.id,
    //   zPosition: 0.001,
    //   dashed: segment.dashed,
    //   wallId: segment.wallId,
    //   side: segment.side,
    //   length: segmentLength.toFixed(3),
    //   isAtConnection: isAtConnection,
    //   actual3DPosition: `(${segment.start.x.toFixed(3)}, ${segment.start.y.toFixed(3)}, 0.001) -> (${segment.end.x.toFixed(3)}, ${segment.end.y.toFixed(3)}, 0.001)`,
    // });

    // Add metadata for interaction and debugging
    segments.userData = {
      type: "wallOutline",
      wallId: segment.wallId,
      side: segment.side,
    };

    // If dashed, compute line distances for proper rendering
    if (segment.dashed) {
      segments.computeLineDistances();
    }

    return segments;
  }, [geometry, material, segment.wallId, segment.side, segment.dashed]);

  // Cleanup function
  React.useEffect(() => {
    //console.log(`  WallOutlineSegment mounted: ${segment.id} (wall: ${segment.wallId})`);
    return () => {
      //console.log(`  WallOutlineSegment unmounted: ${segment.id}`);
      geometry.dispose();
      // Only dispose material if it's not cached
      if (!getCachedMaterial) {
        material.dispose();
      }
    };
  }, [geometry, material, getCachedMaterial, segment.id, segment.wallId]);

  if (!visible) {
    return null;
  }

  const disablePointerEvents = editorStore.wallConstructionMode;
  const handlers = disablePointerEvents ? undefined : getWallInteractionHandlers(segment.wallId);

  return (
    <primitive
      object={lineSegment}
      onPointerDown={handlers?.onPointerDown as ((event: ThreeEvent<PointerEvent>) => void) | undefined}
      onPointerEnter={handlers?.onPointerEnter as ((event: ThreeEvent<PointerEvent>) => void) | undefined}
      onPointerMove={handlers?.onPointerMove as ((event: ThreeEvent<PointerEvent>) => void) | undefined}
      onPointerLeave={handlers?.onPointerLeave as ((event: ThreeEvent<PointerEvent>) => void) | undefined}
    />
  );
};

const MemoizedWallOutlineSegment = React.memo(WallOutlineSegment);
MemoizedWallOutlineSegment.displayName = "WallOutlineSegment";

export default MemoizedWallOutlineSegment;
